import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
  ListItem,
  Button,
  ListItemText,
  IconButton,
  Card,
  CardContent,
  Paper,
  Chip,
  ListItemIcon, // Ensure ListItemIcon is imported if used
} from "@mui/material";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CustomInputSwitch from "./app/CustomInputSwitch"; // Add this import at the top with other imports
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import StartPageFAQSection from "./StartPageFAQSection";
import { East as ArrowIcon } from "@mui/icons-material";
import StarIcon from "@mui/icons-material/Star";
import CheckIcon from "@mui/icons-material/Check";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import { Helmet } from "react-helmet-async";
import { VideoPlayer } from "./app/HelpDialogItems";
import Functions from "./Functions";
import { Link as MuiLink } from "@mui/material";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
// **Newly Added Icon Imports**
import { features } from "./app/functions";

import HelpContent from "./app/HelpContent";

import { Link } from "react-router-dom";
import { TypeAnimation } from "react-type-animation";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import { motion } from "framer-motion";
import { getAIModelImage } from "./app/TestComponents2";

import UniversitySection from "./UniversitySection";
import MediaSection from "./MediaSection";
import FAQSection from "./FAQSection";
import Logger from "./app/Logger";
import TestimonialsSection from "./TestimonialsSection";
import {
  GradientBackground,
  StyledButton,
  FeatureSection,
  FeatureList,
  ResponsiveVideo,
} from "./StyledComponents";

import ContactForm from "./ContactForm";

import { getStyles } from "./index";

import Header from "./Header";
import Footer from "./Footer";

const instructionItems = [
  "Generera interaktiva prov/quiz",
  "Generera flashcards",
  "Låt AI bedöma din text",
  "Lär dig språk med AI",
  "Stöd för över 50 språk",
  "Trygg och säker hantering av personlig integritet och GDPR",
];

const InstructionItem = ({ text }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      mb: 1.5,
    }}
  >
    <Box
      sx={{
        width: 24,
        height: 24,
        borderRadius: "50%",
        backgroundColor: "rgba(255, 255, 255, 0.1)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mr: 2,
      }}
    >
      <CheckIcon sx={{ fontSize: 16, color: "white" }} />
    </Box>
    <Typography
      sx={{
        color: "rgba(255, 255, 255, 0.8)",
        fontSize: "1.1rem",
        lineHeight: 1.5,
        fontWeight: 500,
      }}
    >
      {text}
    </Typography>
  </Box>
);

const StudentsPage = ({ toggleDarkMode, isDark }) => {
  const theme = useTheme();
  const styles = getStyles(theme);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [selectedUserType, setSelectedUserType] = useState(1);

  const contactFormRef = useRef(null); // Add ref for ContactForm

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToContact = (e) => {
    e.preventDefault();
    contactFormRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <Helmet>
        <title>{`AI-verktyg för elever | Studera.ai`}</title>
        <meta
          name="description"
          content="Hitta alla AI-verktyg för elever. Spara oceaner av tid genom att använda AI i skolan."
        />
      </Helmet>

      <Header />

      <GradientBackground className="gradient-background">
        <Container
          maxWidth="xl"
          sx={{
            pt: { xs: 10, md: 21 },
            pb: { xs: 5, md: 2 },
            position: "relative",
          }}
        >
          <Box
            sx={{
              width: { xs: "100%", md: "60%" },
              margin: "0 auto",
              textAlign: "center",
            }}
          >
            <Box sx={{ pl: { xs: 0, md: 4 } }}>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Box sx={{ display: "flex", mb: 4, justifyContent: "center" }}>
                  <Chip
                    label={
                      <span>
                        Studera.ai{" "}
                        <span style={{ opacity: 0.7 }}>
                          &nbsp;•&nbsp; Framtidens lärande
                        </span>
                      </span>
                    }
                    sx={{
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                      color: "white",
                      fontWeight: "600",
                      fontSize: "0.8rem",
                      padding: "2px 14px",

                      borderRadius: "16px",
                      "& .MuiChip-label": {
                        padding: "0 4px",
                      },
                    }}
                  />
                </Box>

                <Typography
                  variant="h2"
                  component="h1"
                  gutterBottom
                  sx={{
                    fontWeight: 700,

                    fontSize: { xs: "2.5rem", md: "4rem" },
                  }}
                >
                  AI-verktyg för elever & studenter
                </Typography>

                <Box sx={{ mt: 4, mb: 0, display: "none" }}>
                  {instructionItems.map((item, index) => (
                    <InstructionItem key={index} text={item} />
                  ))}
                </Box>
                <Box
                  sx={{
                    fontSize: { xs: "1.2rem", md: "1.4rem" },
                    fontWeight: "400",
                    color: "#e5e5e5",

                    width: { xs: "100%", md: "80%" },
                    margin: "0 auto",
                    mt: 3,
                    mb: 3,
                  }}
                >
                  Generera quiz, podcasts, prov och flashcards baserat på ditt
                  studiematerial, chatta med specialiserade assistenter eller
                  lär dig språk — allt med AI.
                </Box>

                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center", // Center everything horizontally on all screen sizes
                    mt: 6,
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center", // Vertically center the content
                    }}
                  >
                    <Box sx={{ display: "flex", ml: -1 }}>
                      {[
                        "https://firebasestorage.googleapis.com/v0/b/studeraai.appspot.com/o/teacherImages%2FZ6BLSMsm38Un5mgW0O6Z3TToHq12%2Fsve.jpg?alt=media&token=58949dc2-887c-4453-b128-b7a55468ca8e",
                        "https://firebasestorage.googleapis.com/v0/b/studeraai.appspot.com/o/teacherImages%2Fimg-P3HIslmb1xtSYDiXeU2bD.jpg?alt=media&token=b4ea68c7-a1de-42f2-a1ac-e06f736f6943",
                        "https://firebasestorage.googleapis.com/v0/b/studeraai.appspot.com/o/misc%2Fl%C3%A4rare%2Fengelska.png?alt=media&token=8c3140b4-1b18-4bb6-bcc3-57039372bf6d",
                      ].map((imgPath, i) => (
                        <Box
                          key={i}
                          component="img"
                          src={imgPath}
                          sx={{
                            width: { xs: 36, sm: 44 },
                            height: { xs: 36, sm: 44 },
                            borderRadius: "50%",
                            border: "2px solid white",
                            objectFit: "cover",
                            ml: i > 0 ? -2 : 0,
                            position: "relative",
                            zIndex: 3 - i,
                          }}
                        />
                      ))}
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        ml: 2,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          color: "#fcd34d",
                        }}
                      >
                        {[0, 1, 2, 3, 4].map((_, i) => (
                          <StarIcon
                            key={i}
                            sx={{
                              width: { xs: 16, sm: 24 },
                              height: { xs: 16, sm: 24 },
                            }}
                          />
                        ))}
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: "500",
                          fontSize: { xs: "0.875rem", sm: "1rem" },
                          color: "#e5e5e5",
                        }}
                      >
                        Uppskattad av 5000+ elever
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    flexDirection: { xs: "column", md: "row" },
                    justifyContent: "center",
                    mb: 8,
                  }}
                >
                  <Link
                    to="/app/skapa-konto?ref=startpage"
                    style={{ textDecoration: "none" }}
                  >
                    <StyledButton
                      variant="blue"
                      color="primary"
                      size="large"
                      sx={{
                        borderRadius: "100px",
                        padding: "13px 50px",
                        fontSize: "20px",
                        width: "300px",
                        fontWeight: "600",
                        mt: 4,
                        backgroundColor: "#eeeeee",
                        borderBottom: "none",
                        color: "#221562",
                        "&:hover": {
                          backgroundColor: "white",
                          color: "#333",
                        },
                      }}
                    >
                      Skapa konto gratis
                    </StyledButton>
                  </Link>

                  <Link
                    to="/app/logga-in?ref=startpage"
                    style={{ textDecoration: "none", display: "none" }}
                  >
                    <StyledButton
                      variant="blue"
                      color="primary"
                      size="large"
                      sx={{
                        borderRadius: "100px",
                        padding: "13px 50px",
                        fontSize: "20px",
                        width: "250px",
                        fontWeight: "600",
                        mt: 4,
                        backgroundColor: "#eeeeee",
                        borderBottom: "none",
                        color: "#221562",
                        "&:hover": {
                          backgroundColor: "white",
                          color: "#333",
                        },
                      }}
                    >
                      Logga in
                    </StyledButton>
                  </Link>
                </Box>
              </motion.div>
            </Box>

            {/* Image on the right */}
          </Box>
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              justifyContent: "center",
              mt: 2,
            }}
          >
            <KeyboardDoubleArrowDownIcon
              sx={{
                fontSize: "4rem",
                color: "#ffffff",
                animation: "bounce 2s infinite",
              }}
            />
          </Box>
        </Container>
      </GradientBackground>

      <Box
        sx={{
          bgcolor: "background.default",
        }}
      >
        <Functions selectedUserType={selectedUserType} />
      </Box>
      <Box
        sx={{
          bgcolor: "background.default",
          py: 8,
          mt: { xs: 2, md: 8 },
          pb: 14,
          display: "none",
        }}
      >
        <Box sx={{ width: { xs: "100%", md: "1300px" }, margin: "0 auto" }}>
          <Typography
            variant="h3"
            align="center"
            gutterBottom
            sx={{
              fontWeight: "bold",
              fontSize: { xs: "2.2rem", md: "3.2em" },
            }}
          >
            Se vad som finns
          </Typography>

          <Typography
            variant="h5"
            sx={{
              mb: 4,
              opacity: 0.8,
              fontSize: "1.3rem",

              margin: "0 auto",
              fontWeight: "500",
              lineHeight: "31px",
              display: "block",
              mt: 2,
              textAlign: "center",
            }}
          >
            Välj en sektion och en funktion
          </Typography>
          <Box sx={{ mt: { xs: 4, md: 8 } }}>
            <HelpContent
              overview={true}
              backgroundGradient="linear-gradient(105deg, rgb(95 74 216) 0%, rgb(77 58 176) 50%, rgb(78 59 179) 100%)"
              clipPathBackground=""
              user={{ accountType: "student" }}
            />
          </Box>
        </Box>
      </Box>

      {features
        .filter((feature) => {
          const hideCheck = !feature.hideOnStartpage;

          // Then check user type visibility
          let showForCheck;
          if (feature.showFor) {
            const userTypeString =
              selectedUserType === 0 ? "teacher" : "student";

            showForCheck = feature.showFor.includes(userTypeString);
          } else {
            showForCheck =
              selectedUserType === 0 ? feature.teacher : !feature.teacher;
          }

          // Also check that the feature has all required properties
          const hasRequiredProps =
            feature.details &&
            feature.img &&
            feature.img.light &&
            feature.img.dark;

          return hideCheck && showForCheck && hasRequiredProps;
        })
        .map((feature, index) => {
          const hasModels = Boolean(
            feature.models && feature.models.length > 0,
          );
          const isImageRight = index % 2 === 0;
          return (
            <FeatureSection
              key={index}
              id={feature.title.toLowerCase().replace(/ /g, "-")}
              sx={{
                bgcolor:
                  index % 2 === 0 ? "background.default" : "background.paper",
                paddingTop: index === 0 ? "0px" : undefined,
              }}
            >
              <Container maxWidth="xl" sx={{ overflow: "hidden" }}>
                <Grid
                  container
                  spacing={0}
                  alignItems="center"
                  direction={isImageRight ? "row" : "row-reverse"}
                  sx={{
                    paddingTop: { xs: "0px", md: "80px" },
                    paddingBottom: { xs: "0px", md: "80px" },
                    paddingLeft: {
                      xs: "0px",
                      md: isImageRight ? "30px" : "0px",
                    },
                  }}
                >
                  {/* Text Content */}
                  <Grid
                    item
                    xs={12}
                    md={hasModels ? 12 : 6}
                    sx={{
                      textAlign: hasModels ? "center" : "left",
                    }}
                  >
                    <Typography
                      variant="h3"
                      gutterBottom
                      sx={{
                        fontWeight: "bold",
                        fontSize: { xs: "1.8em", md: "2.3em" },
                        textAlign: hasModels ? "center" : "left",
                      }}
                    >
                      {feature.title.split(" ").map((word, index) => (
                        <span
                          key={index}
                          style={
                            word === feature.highightedWord
                              ? {
                                  backgroundClip: "text",
                                  textFillColor: "transparent",
                                  background:
                                    "linear-gradient(135deg, #43CBFF 10%, #9708CC 100%)",
                                  WebkitBackgroundClip: "text",
                                  WebkitTextFillColor: "transparent",
                                }
                              : {}
                          }
                        >
                          {word}{" "}
                        </span>
                      ))}
                    </Typography>
                    <Typography
                      variant="h6"
                      paragraph
                      color="text.secondary"
                      sx={{
                        textAlign: hasModels ? "center" : "left",
                      }}
                    >
                      {feature.description}
                    </Typography>

                    {hasModels ? (
                      // If feature has models, render them
                      <Grid
                        container
                        spacing={2}
                        justifyContent="center"
                        sx={{ marginTop: hasModels ? "20px" : "inherit" }}
                      >
                        {feature.models.map((model, idx) => (
                          <Grid item xs={12} sm={6} md={4} key={idx}>
                            <Card
                              sx={{
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                padding: 2,
                                boxShadow: "none",
                                transition: "transform 0.3s, box-shadow 0.3s",
                                "&:hover": {
                                  transform: "translateY(-10px)",
                                },
                              }}
                            >
                              <CardContent sx={{ textAlign: "center" }}>
                                <Box
                                  component="img"
                                  src={getAIModelImage(model.name)}
                                  alt={model.name}
                                  sx={{
                                    width: 60,
                                    height: 60,
                                    mb: 2,
                                    borderRadius: "9px",
                                  }}
                                />
                                <Typography variant="h6" gutterBottom>
                                  {model.name}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {model.description}
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      // If feature has details, render them
                      <FeatureList
                        sx={{
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "transparent"
                              : "inherit",
                        }}
                      >
                        {feature.details &&
                          feature.details
                            .filter(
                              (detail) => detail.showInFeaturePage !== true,
                            )
                            .map((detail, idx) => (
                              <ListItem
                                key={idx}
                                alignItems="flex-start"
                                sx={{ marginLeft: "0px", paddingLeft: "0px" }}
                              >
                                <ListItemIcon
                                  sx={{
                                    "& > svg": {
                                      fontSize: "34px",
                                      color:
                                        theme.palette.mode === "dark"
                                          ? "#9575F7"
                                          : "#6424c1",
                                    },
                                    minWidth: "40px",
                                    marginRight: "15px",
                                    marginLeft: "0px",
                                  }}
                                >
                                  {detail.icon}
                                </ListItemIcon>
                                <ListItemText
                                  primary={
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontWeight: "600",
                                          fontSize: "1.2rem",
                                        }}
                                      >
                                        {detail.text.split(":")[0]}
                                      </Typography>
                                      {detail.exampleUrl && (
                                        <Box
                                          component="a"
                                          href={detail.exampleUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          sx={{
                                            display: {
                                              xs: "none",
                                              md: "undefined",
                                            },
                                            textDecoration: "none",
                                            ml: 2,
                                            pointerEvents: "auto !important",
                                            "& .MuiChip-root": {
                                              pointerEvents: "auto !important",
                                            },
                                          }}
                                        >
                                          <Chip
                                            label={
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: 0.5,
                                                  pointerEvents:
                                                    "auto !important",
                                                }}
                                              >
                                                Visa exempel
                                                <OpenInNew
                                                  sx={{ fontSize: 14 }}
                                                />
                                              </Box>
                                            }
                                            size="small"
                                            sx={{
                                              ...styles.greenChip,
                                              height: "24px",
                                              cursor: "pointer",
                                              pointerEvents: "auto !important",
                                              "&:hover": {
                                                backgroundColor: `${theme.palette.primary.main} !important`,
                                                color: "white !important",
                                              },
                                            }}
                                          />
                                        </Box>
                                      )}
                                    </Box>
                                  }
                                  secondary={
                                    <Typography
                                      sx={{
                                        fontSize: "1rem",
                                        fontWeight: "500",
                                        mt: 0.5,
                                        color:
                                          theme.palette.mode === "dark"
                                            ? "#8899a6"
                                            : "gray",
                                      }}
                                    >
                                      {detail.text.split(":")[1]}
                                    </Typography>
                                  }
                                />
                              </ListItem>
                            ))}
                      </FeatureList>
                    )}

                    <Box
                      mt={4}
                      display="flex"
                      justifyContent={hasModels ? "center" : "flex-start"}
                      sx={{ display: "none" }}
                    >
                      <Link
                        to={
                          feature.goDirectlyToApp
                            ? "/app/skapa-konto?ref=startpage"
                            : feature.customLink
                              ? `/funktioner/${feature.customLink}`
                              : `/funktioner/${feature.title.toLowerCase().replace(/ /g, "-").replace(/å/g, "a").replace(/ä/g, "a").replace(/ö/g, "o")}`
                        }
                        style={{ textDecoration: "none" }}
                      >
                        <Button variant="purp" endIcon={<ArrowForwardIcon />}>
                          Läs mer
                        </Button>
                      </Link>
                    </Box>
                  </Grid>

                  {/* Image or Video Content */}
                  {!hasModels && (
                    <Grid
                      item
                      xs={12}
                      md={6}
                      sx={{
                        position: "relative",
                        mt: { xs: 5, md: undefined },
                      }}
                    >
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        viewport={{ once: true, amount: 0.3 }}
                      >
                        <Box
                          sx={{
                            position: "relative",
                            width: "calc(100% + 35px)",
                            padding: "25px", // This creates space for our border

                            background:
                              "linear-gradient(135deg, rgba(85, 64, 196, 0.6), rgba(64, 132, 196, 0.6))",
                            borderRadius: "23px",
                            ...(isImageRight
                              ? { right: "-58px", mr: "-35px" }
                              : { left: "-58px", ml: "-35px" }),
                          }}
                        >
                          {feature.img && (
                            <Box
                              component="img"
                              src={
                                theme.palette.mode === "dark"
                                  ? feature.img.dark
                                  : feature.img.light
                              }
                              alt={`${feature.title}`}
                              sx={{
                                width: "100%",
                                height: "auto",
                                borderRadius: "15px",
                                display: "block",
                              }}
                            />
                          )}
                        </Box>
                      </motion.div>
                    </Grid>
                  )}
                </Grid>
              </Container>
            </FeatureSection>
          );
        })}

      <Box
        sx={{ bgcolor: "background.default", py: 8, mt: 8, display: "none" }}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={10}
            alignItems="center"
            justifyContent="center"
          >
            <Grid
              item
              xs={12}
              md={10}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Typography
                variant="h3"
                align="center"
                sx={{
                  fontWeight: "bold",
                  fontSize: "2.3em",
                  mb: 2,
                }}
              >
                Vanliga frågor och svar
              </Typography>

              <Typography
                variant="h6"
                paragraph
                color="text.secondary"
                sx={{ textAlign: "center", mb: 3 }}
              >
                Om du har några andra frågor — vänligen{" "}
                <MuiLink
                  onClick={scrollToContact}
                  sx={{
                    color: "text.secondary",
                    textDecoration: "none",
                    cursor: "pointer",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  kontakta oss
                </MuiLink>
              </Typography>

              <Box sx={{ width: "100%" }}>
                <StartPageFAQSection student={true} />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box
        sx={{
          bgcolor: "background.paper",
          py: 8,
          paddingTop: "100px",
          position: "relative",
          display: "none",
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            align="center"
            sx={{ fontWeight: "bold", fontSize: "2.3em", mb: 2 }}
          >
            Upptäck möjligheterna med Studera.ai
          </Typography>
          <Typography
            variant="subtitle1"
            align="center"
            color="text.secondary"
            sx={{ mb: 4 }}
          >
            Utforska några av de populäraste funktionerna
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <KeyboardDoubleArrowDownIcon
              sx={{
                fontSize: "4rem",
                color: "#6424c1",
                animation: "bounce 2s infinite",
              }}
            />
          </Box>
        </Container>

        <style>
          {`
      @keyframes bounce {
        0%, 20%, 50%, 80%, 100% {
          transform: translateY(0);
        }
        40% {
          transform: translateY(-30px);
        }
        60% {
          transform: translateY(-15px);
        }
      }
    `}
        </style>
      </Box>

      <Container
        maxWidth="xl"
        sx={{
          paddingTop: 22,
          paddingBottom: 22,
          textAlign: "center",

          backgroundImage:
            'url("/img/hepp.svg"), radial-gradient(circle, rgb(106, 80, 243), rgb(62, 46, 141) 82%)',
        }}
      >
        <Typography
          variant="h3"
          gutterBottom
          sx={{
            fontWeight: "bold",
            fontSize: "2.6em",
            color: "white",
            display: "block",
            mb: 3,
          }}
        >
          Prova helt gratis
        </Typography>
        <Typography variant="h6" sx={{ mb: 4, color: "#E7E7E8" }}>
          Anslut dig till 3000+ lärare och elever som använder Studera.ai
        </Typography>
        <Link
          to="/app/skapa-konto?ref=startpage"
          style={{ textDecoration: "none" }}
        >
          <StyledButton
            variant="blue"
            color="primary"
            size="large"
            endIcon={<KeyboardDoubleArrowRightIcon />}
            sx={{
              borderRadius: "12px",
              padding: "13px 50px",
              fontSize: "20px",
            }}
          >
            Skapa konto helt gratis
          </StyledButton>
        </Link>
      </Container>
      <div ref={contactFormRef}>
        <ContactForm showMoreFields={true} />
      </div>

      <TestimonialsSection />
      <Box sx={{ bgcolor: "background.default", py: 8, mt: 8 }}>
        <Container maxWidth="lg">
          <Grid
            container
            spacing={10}
            alignItems="center"
            justifyContent="center"
          >
            <Grid
              item
              xs={12}
              md={10}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Typography
                variant="h3"
                align="center"
                sx={{
                  fontWeight: "bold",
                  fontSize: "2.3em",
                  mb: 2,
                }}
              >
                Vanliga frågor och svar
              </Typography>

              <Typography
                variant="h6"
                paragraph
                color="text.secondary"
                sx={{ textAlign: "center", mb: 3 }}
              >
                Om du har några andra frågor — vänligen{" "}
                <MuiLink
                  onClick={scrollToContact}
                  sx={{
                    color: "text.secondary",
                    textDecoration: "none",
                    cursor: "pointer",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  kontakta oss
                </MuiLink>
              </Typography>

              <Box sx={{ width: "100%" }}>
                <StartPageFAQSection student={true} />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <TestimonialsSection />

      <UniversitySection bgcolor="background.default" showHeadline={true} />

      <MediaSection bgcolor="background.default" />

      <Footer />

      {!isMobile && (
        <Box
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
            zIndex: 9999,
          }}
        >
          <IconButton onClick={toggleDarkMode}>
            {isDark ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
        </Box>
      )}
    </>
  );
};

export default StudentsPage;
